import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Row, Col, Badge, Spinner, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getShopByOwner, getShopOrders, updateOrderStatus, updateOrderItem, updateOrderShipping } from '../../services/shopService';
import { getUserProfile } from '../../services/userService';
import { sendOrderConfirmationToCustomer } from '../../services/emailService';
import { renderProductImage } from '../../utils/imageHelpers';
import CustomToast from '../../components/Toast';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

const OrderDetail = () => {
  const { orderId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [shop, setShop] = useState(null);
  const [order, setOrder] = useState(null);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [editingItem, setEditingItem] = useState(null);
  const [editingQuantity, setEditingQuantity] = useState(0);
  const [editingShipping, setEditingShipping] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    loadShopAndOrder();
  }, [currentUser, orderId]);

  const loadShopAndOrder = async () => {
    try {
      setLoading(true);
      const shops = await getShopByOwner(currentUser.uid);
      if (shops.length > 0) {
        setShop(shops[0]);
        const ordersList = await getShopOrders(shops[0].id);
        const foundOrder = ordersList.find(o => o.id === orderId);
        if (foundOrder) {
          setOrder(foundOrder);
          // Carica il profilo del cliente per mostrare indirizzo e contatti
          if (foundOrder.customerId) {
            try {
              const profile = await getUserProfile(foundOrder.customerId);
              setCustomerProfile(profile);
            } catch (err) {
              console.error('Errore nel caricamento del profilo cliente:', err);
            }
          }
        } else {
          setError('Ordine non trovato');
        }
      }
    } catch (error) {
      console.error('Errore nel caricamento dell\'ordine:', error);
      setError(t('errors.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      // Se stiamo confermando l'ordine, aggiorniamo anche gli items non disponibili
      if (newStatus === 'confirmed') {
        const updatedItems = order.items.map(item => ({
          ...item,
          isAvailable: item.isAvailable === false ? false : true
        }));

        // Ricalcola il subtotale escludendo i prodotti non disponibili
        const subtotal = updatedItems.reduce((total, item) => 
          total + (item.isAvailable !== false ? item.price * item.quantity : 0), 0
        );
        const total = subtotal + (order.shippingCost || 0);

        // Aggiorna l'ordine nel database
        await updateDoc(doc(db, 'shops', shop.id, 'orders', order.id), {
          status: newStatus,
          items: updatedItems,
          subtotal,
          total,
          updatedAt: new Date().toISOString()
        });

        // Aggiorna lo stato locale
        setOrder(prevOrder => ({
          ...prevOrder,
          status: newStatus,
          items: updatedItems,
          subtotal,
          total
        }));

        // Invia email di conferma al cliente con la lista aggiornata
        const customerEmail = order.customerEmail || customerProfile?.email;
        if (customerEmail) {
          sendOrderConfirmationToCustomer({
            customerEmail,
            customerName: order.customerName,
            shopName: shop.name,
            orderNumber: order.orderNumber,
            total,
            items: updatedItems,
            shippingCost: order.shippingCost || 0
          }).catch(err => console.error('Errore invio email conferma:', err));
        }
      } else {
        await updateOrderStatus(shop.id, order.id, newStatus);
        setOrder(prevOrder => ({ ...prevOrder, status: newStatus }));
      }

      setToastMessage('Stato ordine aggiornato con successo');
      setToastVariant('success');
      setShowToast(true);
    } catch (error) {
      console.error('Errore nell\'aggiornamento dello stato:', error);
      setToastMessage('Errore nell\'aggiornamento dello stato');
      setToastVariant('danger');
      setShowToast(true);
    }
  };

  const handleQuantityChange = async (item, newQuantity) => {
    if (newQuantity < 0) return;
    
    try {
      setIsUpdating(true);
      await updateOrderItem(shop.id, order.id, item.id, {
        quantity: newQuantity,
        price: item.price
      });
      
      // Aggiorna l'ordine locale
      setOrder(prevOrder => ({
        ...prevOrder,
        items: prevOrder.items.map(i => 
          i.id === item.id ? { ...i, quantity: newQuantity } : i
        ),
        subtotal: prevOrder.items.reduce((total, i) => 
          total + (i.id === item.id ? i.price * newQuantity : i.price * i.quantity), 0
        )
      }));
      
      setToastMessage('Quantità aggiornata con successo');
      setToastVariant('success');
      setShowToast(true);
    } catch (error) {
      console.error('Errore nell\'aggiornamento della quantità:', error);
      setToastMessage('Errore nell\'aggiornamento della quantità');
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleShippingChange = async (newShipping) => {
    try {
      setIsUpdating(true);
      await updateOrderShipping(shop.id, order.id, newShipping);
      
      // Aggiorna l'ordine locale
      setOrder(prevOrder => ({
        ...prevOrder,
        shippingCost: newShipping,
        total: prevOrder.subtotal + newShipping
      }));
      
      setToastMessage('Costi di spedizione aggiornati con successo');
      setToastVariant('success');
      setShowToast(true);
    } catch (error) {
      console.error('Errore nell\'aggiornamento dei costi di spedizione:', error);
      setToastMessage('Errore nell\'aggiornamento dei costi di spedizione');
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleItemAvailability = async (item, makeAvailable = false) => {
    try {
      setIsUpdating(true);
      await updateOrderItem(shop.id, order.id, item.id, {
        ...item,
        isAvailable: makeAvailable ? true : false
      });
      
      // Aggiorna l'ordine locale
      setOrder(prevOrder => {
        const updatedItems = prevOrder.items.map(i => 
          i.id === item.id ? { ...i, isAvailable: makeAvailable ? true : false } : i
        );
        
        // Ricalcola il subtotale escludendo i prodotti non disponibili
        const subtotal = updatedItems.reduce((total, i) => 
          total + (i.isAvailable !== false ? i.price * i.quantity : 0), 0
        );
        const total = subtotal + (prevOrder.shippingCost || 0);

        return {
          ...prevOrder,
          items: updatedItems,
          subtotal,
          total
        };
      });
      
      setToastMessage(makeAvailable ? 'Prodotto ripristinato come disponibile' : 'Prodotto segnato come non disponibile');
      setToastVariant('success');
      setShowToast(true);
    } catch (error) {
      console.error('Errore nell\'aggiornamento della disponibilità:', error);
      setToastMessage('Errore nell\'aggiornamento della disponibilità');
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending_confirmation': { variant: 'warning', text: 'In attesa di conferma' },
      'confirmed': { variant: 'success', text: 'Confermato' },
      'processing': { variant: 'info', text: 'In elaborazione' },
      'shipped': { variant: 'primary', text: 'Spedito' },
      'delivered': { variant: 'success', text: 'Consegnato' },
      'cancelled': { variant: 'danger', text: 'Annullato' }
    };

    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return (
      <Badge bg={config.variant} className="px-3 py-2">
        {config.text}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!shop || !order) {
    return (
      <Container className="py-5">
        <Card>
          <Card.Body className="text-center">
            <h3>{error || t('errors.noShopFound')}</h3>
            <Button variant="primary" onClick={() => navigate('/seller/orders')}>
              Torna agli ordini
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <CustomToast 
        show={showToast} 
        onClose={() => setShowToast(false)} 
        message={toastMessage}
        variant={toastVariant}
      />

      <div className="mb-3" style={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          variant="dark"
          className="rounded-circle shadow-sm border-0"
          style={{ width: 44, height: 44, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => navigate('/seller/orders')}
        >
          <i className="bi bi-arrow-left fs-4 text-white"></i>
        </Button>
      </div>

      <h2 className="mb-4">Dettaglio Ordine</h2>

      {/* Info Ordine e Cliente */}
      <Card className="mb-4 rounded-4 border-0 shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h5 className="fw-bold mb-1">Ordine #{order.orderNumber?.toString().padStart(6, '0')}</h5>
              <p className="text-muted small mb-0">{order.customerName}</p>
              <p className="text-muted small mb-0">{order.customerEmail || customerProfile?.email}</p>
            </div>
            {getStatusBadge(order.status)}
          </div>

          {/* Info cliente */}
          {customerProfile && (
            <div className="mb-3 p-3 rounded-3" style={{ backgroundColor: '#f8f9fa' }}>
              <h6 className="fw-bold mb-2">
                <i className="bi bi-person me-2"></i>Informazioni Cliente
              </h6>
              <Row>
                {customerProfile.phone && (
                  <Col xs={12} md={6} className="mb-1">
                    <small className="text-muted">
                      <i className="bi bi-telephone me-1"></i>
                      Numero: {customerProfile.phone}
                    </small>
                  </Col>
                )}
                {customerProfile.messagingId && (
                  <Col xs={12} md={6} className="mb-1">
                    <small className="text-muted">
                      <i className="bi bi-wechat me-1"></i>
                      WeChat/WhatsApp: {customerProfile.messagingId}
                    </small>
                  </Col>
                )}
                {customerProfile.intercom && (
                  <Col xs={12} md={6} className="mb-1">
                    <small className="text-muted">
                      <i className="bi bi-bell me-1"></i>
                      Citofono: {customerProfile.intercom}
                    </small>
                  </Col>
                )}
                {customerProfile.floor && (
                  <Col xs={12} md={6} className="mb-1">
                    <small className="text-muted">
                      <i className="bi bi-buildings me-1"></i>
                      Interno/Scala: {customerProfile.floor}
                    </small>
                  </Col>
                )}
              </Row>
              {(customerProfile.address || customerProfile.city) && (
                <div className="mt-2">
                  <small className="text-muted">
                    <i className="bi bi-geo-alt me-1"></i>
                    Indirizzo: {[
                      customerProfile.address,
                      customerProfile.city,
                      customerProfile.zipCode
                    ].filter(Boolean).join(', ')}
                  </small>
                </div>
              )}
            </div>
          )}

          {/* Se non c'è un profilo cliente, mostra comunque i dati base dall'ordine */}
          {!customerProfile && (order.phone || order.customerEmail || order.messagingId || order.address || order.intercom || order.floor) && (
            <div className="mb-3 p-3 rounded-3" style={{ backgroundColor: '#f8f9fa' }}>
              <h6 className="fw-bold mb-2">
                <i className="bi bi-person me-2"></i>Informazioni Cliente
              </h6>
              <Row>
                {order.phone && (
                  <Col xs={12} md={6} className="mb-1">
                    <small className="text-muted">
                      <i className="bi bi-telephone me-1"></i>
                      Numero: {order.phone}
                    </small>
                  </Col>
                )}
                {order.messagingId && (
                  <Col xs={12} md={6} className="mb-1">
                    <small className="text-muted">
                      <i className="bi bi-wechat me-1"></i>
                      WeChat/WhatsApp: {order.messagingId}
                    </small>
                  </Col>
                )}
                {order.intercom && (
                  <Col xs={12} md={6} className="mb-1">
                    <small className="text-muted">
                      <i className="bi bi-bell me-1"></i>
                      Citofono: {order.intercom}
                    </small>
                  </Col>
                )}
                {order.floor && (
                  <Col xs={12} md={6} className="mb-1">
                    <small className="text-muted">
                      <i className="bi bi-buildings me-1"></i>
                      Interno/Scala: {order.floor}
                    </small>
                  </Col>
                )}
              </Row>
              {order.address && (
                <div className="mt-2">
                  <small className="text-muted">
                    <i className="bi bi-geo-alt me-1"></i>
                    Indirizzo: {order.address}
                  </small>
                </div>
              )}
            </div>
          )}

          {/* Stato Pagamento */}
          {order.paymentMethod && (
            <div className="mb-3 p-3 rounded-3" style={{ backgroundColor: order.paymentStatus === 'paid' ? '#D4EDDA' : '#FFF3CD' }}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="fw-bold mb-1">
                    <i className="bi bi-credit-card me-2"></i>
                    Pagamento: {order.paymentMethod}
                  </h6>
                  <Badge bg={order.paymentStatus === 'paid' ? 'success' : 'warning'} className="mt-1">
                    {order.paymentStatus === 'paid' ? 'Pagamento verificato' : 'In attesa di verifica'}
                  </Badge>
                </div>
                {order.paymentStatus === 'pending' && (
                  <Button
                    variant="success"
                    size="sm"
                    className="rounded-pill"
                    onClick={async () => {
                      try {
                        await updateDoc(doc(db, 'shops', shop.id, 'orders', order.id), {
                          paymentStatus: 'paid',
                          updatedAt: new Date().toISOString()
                        });
                        setOrder(prev => ({ ...prev, paymentStatus: 'paid' }));
                        setToastMessage('Pagamento verificato con successo');
                        setToastVariant('success');
                        setShowToast(true);
                      } catch (err) {
                        console.error('Errore verifica pagamento:', err);
                        setToastMessage('Errore nella verifica del pagamento');
                        setToastVariant('danger');
                        setShowToast(true);
                      }
                    }}
                  >
                    <i className="bi bi-check-lg me-1"></i>
                    Verifica pagamento
                  </Button>
                )}
              </div>
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center">
            <div>
              <p className="text-muted small mb-0">Data</p>
              <p className="mb-0">{formatDate(order.createdAt)}</p>
            </div>
            <div className="text-end">
              <p className="text-muted small mb-0">Totale</p>
              <p className="mb-0 fw-bold">€{order.total.toFixed(2)}</p>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Prodotti */}
      <h5 className="mb-3 fw-bold">Prodotti</h5>
      <Card className="mb-4 rounded-4 border-0 shadow-sm">
        <Card.Body>
          {order.items.map((item, index) => (
            <React.Fragment key={item.id}>
              {index > 0 && <hr className="my-3" />}
              <Row className="align-items-center">
                <Col xs={3}>
                  <div className="rounded-4 overflow-hidden bg-white position-relative" style={{ width: '70px', height: '70px' }}>
                    {renderProductImage(item.image, item.name)}
                    {item.isAvailable === false && (
                      <div 
                        className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center" 
                        style={{ 
                          top: 0, 
                          left: 0, 
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          backdropFilter: 'blur(2px)'
                        }}
                      >
                        <i className="bi bi-slash-circle text-white" style={{ fontSize: '1.5rem' }}></i>
                      </div>
                    )}
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="d-flex align-items-center mb-1">
                    <p className="mb-0 fw-medium text-dark" style={{ opacity: item.isAvailable === false ? 0.5 : 1 }}>{item.name}</p>
                  </div>
                  <p className="text-primary mb-0 fw-bold" style={{ opacity: item.isAvailable === false ? 0.5 : 1 }}>€{item.price.toFixed(2)}</p>
                  <div className="d-flex align-items-center mt-2">
                    <div style={{ background: '#F2F2F2', borderRadius: 32, display: 'flex', alignItems: 'center', padding: '4px 4px' }}>
                      <Button
                        variant="light"
                        className="rounded-circle border-0 d-flex align-items-center justify-content-center"
                        style={{ width: 32, height: 32, fontSize: 16, padding: 0 }}
                        onClick={() => handleQuantityChange(item, item.quantity - 1)}
                        disabled={item.quantity <= 1 || isUpdating}
                      >
                        <i className="bi bi-dash"></i>
                      </Button>
                      <span className="mx-2 fw-bold" style={{ fontSize: 14, minWidth: 20, textAlign: 'center' }}>{item.quantity}</span>
                      <Button
                        variant="light"
                        className="rounded-circle border-0 d-flex align-items-center justify-content-center"
                        style={{ width: 32, height: 32, fontSize: 16, padding: 0 }}
                        onClick={() => handleQuantityChange(item, item.quantity + 1)}
                        disabled={isUpdating}
                      >
                        <i className="bi bi-plus"></i>
                      </Button>
                    </div>
                    {item.isAvailable !== false ? (
                      <Button
                        variant="light"
                        className="rounded-circle border-0 d-flex align-items-center justify-content-center ms-2"
                        style={{ width: 40, height: 40, fontSize: 16, padding: 0, backgroundColor: '#F2F2F2' }}
                        onClick={() => handleItemAvailability(item)}
                        disabled={isUpdating}
                      >
                        <i className="bi bi-x-circle text-danger"></i>
                      </Button>
                    ) : (
                      <Button
                        variant="light"
                        className="rounded-circle border-0 d-flex align-items-center justify-content-center ms-2"
                        style={{ width: 40, height: 40, fontSize: 16, padding: 0, backgroundColor: '#F2F2F2' }}
                        onClick={() => handleItemAvailability(item, true)}
                        disabled={isUpdating}
                      >
                        <i className="bi bi-check-circle text-success"></i>
                      </Button>
                    )}
                  </div>
                </Col>
                <Col xs={3} className="text-end">
                  <p className="fw-bold mb-0 text-dark" style={{ opacity: item.isAvailable === false ? 0.5 : 1 }}>€{(item.price * item.quantity).toFixed(2)}</p>
                </Col>
              </Row>
            </React.Fragment>
          ))}
        </Card.Body>
      </Card>

      {/* Dettagli Ordine */}
      <h5 className="mb-3 fw-bold">Riepilogo</h5>
      <Card className="mb-4 rounded-4 border-0 shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between mb-2">
            <span className="text-secondary">Subtotale</span>
            <span className="text-dark">€{order.subtotal.toFixed(2)}</span>
          </div>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-secondary">Spedizione</span>
            <div className="d-flex align-items-center">
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={order.shippingCost}
                onChange={(e) => handleShippingChange(parseFloat(e.target.value))}
                style={{ width: '100px' }}
                disabled={isUpdating}
              />
              <span className="ms-2">€</span>
            </div>
          </div>
          <hr />
          <div className="d-flex justify-content-between">
            <span className="fw-bold text-dark">Totale</span>
            <span className="fw-bold text-dark">€{order.total.toFixed(2)}</span>
          </div>
        </Card.Body>
      </Card>

      {/* Azioni */}
      <div className="d-flex gap-2">
        {order.status === 'pending_confirmation' && (
          <>
            <Button
              variant="outline-success"
              className="flex-grow-1"
              onClick={() => handleStatusUpdate('confirmed')}
            >
              <i className="bi bi-check-lg me-2"></i>
              Conferma
            </Button>
            <Button
              variant="outline-danger"
              className="flex-grow-1"
              onClick={() => handleStatusUpdate('cancelled')}
            >
              <i className="bi bi-x-lg me-2"></i>
              Annulla
            </Button>
          </>
        )}
        {order.status === 'confirmed' && (
          <Button
            variant="outline-info"
            className="flex-grow-1"
            onClick={() => handleStatusUpdate('processing')}
          >
            <i className="bi bi-gear me-2"></i>
            Elabora
          </Button>
        )}
        {order.status === 'processing' && (
          <Button
            variant="outline-primary"
            className="flex-grow-1"
            onClick={() => handleStatusUpdate('shipped')}
          >
            <i className="bi bi-truck me-2"></i>
            Spedisci
          </Button>
        )}
        {order.status === 'shipped' && (
          <Button
            variant="outline-success"
            className="flex-grow-1"
            onClick={() => handleStatusUpdate('delivered')}
          >
            <i className="bi bi-check-circle me-2"></i>
            Consegnato
          </Button>
        )}
      </div>
    </Container>
  );
};

export default OrderDetail; 